import React from 'react';
import { Tooltip } from 'antd';
import { FolderOpenOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { decouperCorps } from '../../services/discussionService';
import { CHARTE_COLORS as C } from '../../variables/constants';

/**
 * Rendu d'un corps de message : texte brut + puces de mention.
 *
 * RENDU EN TEXTE, TOUJOURS. Aucun `dangerouslySetInnerHTML`, aucun Markdown : le
 * corps est une chaîne qu'un utilisateur contrôle entièrement, et l'interpréter
 * ouvrirait une injection dans un écran qui affiche des dossiers nominatifs. Les
 * puces ne viennent pas d'une balise du texte mais d'un découpage en fragments,
 * chacun rendu comme un nœud React.
 *
 * `whiteSpace: pre-wrap` sur le conteneur : les retours à la ligne saisis par
 * l'agent sont significatifs - une énumération de points à vérifier perd son sens
 * repliée en un paragraphe.
 */

/**
 * Une mention non délivrée s'affiche en sourdine. Elle survient quand la personne
 * nommée n'a pas accès au dossier : afficher la puce comme les autres laisserait
 * croire que l'alerte est partie, la masquer ferait disparaître un mot du message.
 */
const estDelivree = (cible, mentions) => {
  if (!cible?.startsWith('utilisateur:')) return true; // les rôles sont résolus côté serveur
  const id = cible.slice('utilisateur:'.length);
  return (mentions || []).some((m) => m.utilisateurTrackingId === id);
};

export default function CorpsMessage({ corps, mentions, moiTrackingId }) {
  const { t } = useTranslation('commun');
  const navigate = useNavigate();
  const fragments = decouperCorps(corps);

  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {fragments.map((f, i) => {
        if (f.type === 'texte') return <React.Fragment key={i}>{f.valeur}</React.Fragment>;
        // Dossier cité par `#` : le marqueur porte l'identifiant, le clic ouvre ce
        // dossier sans rien rechercher (le numéro a été réécrit par le serveur).
        if (f.type === 'dossier') {
          return (
            <button
              key={i}
              type="button"
              title={t('corpsMessage.ouvrirDossier', { numero: f.valeur })}
              onClick={() => navigate(`/sinistres/${f.sinistreTrackingId}`)}
              style={{
                font: 'inherit', fontWeight: 600, fontSize: '0.94em', color: C.green,
                background: C.greenLight, border: '1px solid #c7e3d1', borderRadius: 5,
                padding: '0 5px', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <FolderOpenOutlined style={{ fontSize: '0.9em', marginRight: 3 }} />{f.valeur}
            </button>
          );
        }

        const delivree = estDelivree(f.cible, mentions);
        // Ma propre mention ressort davantage : dans un fil de quarante messages,
        // ce qui compte d'abord est de retrouver ce qui m'était adressé.
        const cEstMoi = moiTrackingId && f.cible === `utilisateur:${moiTrackingId}`;

        const puce = (
          <span
            key={i}
            style={{
              background: !delivree ? '#f3f4f6' : cEstMoi ? '#fde68a' : C.greenLight,
              color: !delivree ? C.faint : cEstMoi ? '#78350f' : C.green,
              border: `1px solid ${!delivree ? C.border : cEstMoi ? '#fcd34d' : '#c7e3d1'}`,
              borderRadius: 5,
              padding: '0 5px',
              fontWeight: 600,
              fontSize: '0.94em',
              whiteSpace: 'nowrap',
            }}
          >
            @{f.valeur}
          </span>
        );

        return delivree ? puce : (
          <Tooltip key={i} title={t('corpsMessage.mentionNonDelivree')}>
            {puce}
          </Tooltip>
        );
      })}
    </span>
  );
}
